import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import displayAdLoaded from '../../action/displayAdLoaded';
import getScrollTop from '../../util/getScrollTop';
import { appContext } from '../../util/decorators';
import pubSub from '../../util/pubSub';

const AD_HEIGHTS = {};

@connect()
@appContext
export default class AdUnit extends React.PureComponent {
  static propTypes = {
    id: React.PropTypes.string,
    value: React.PropTypes.string.isRequired,
    displayAdCount: React.PropTypes.number,
    dispatch: React.PropTypes.func.isRequired,
    callback: React.PropTypes.func,
    isActiveSlot: React.PropTypes.bool,
    disable: React.PropTypes.bool,
    maxWidth: React.PropTypes.number,
    country: React.PropTypes.string,
    headerHeight: React.PropTypes.number,
  };

  constructor(props, context) {
    super(props);

    this.state = {
      width: '100%',
      maxWidth: 'none',
      containerWidth: 'auto',
      marginLeft: 0,
      showAd: false,
      disabled: '',
      backgroundColor: '',
    };

    this.freewheelCheck = this.freewheelCheck.bind(this);
    this.adCheck = this.adCheck.bind(this);
    this.disable = this.disable.bind(this);
    this.receiveMessage = this.receiveMessage.bind(this);

    this.adId = this.props.id || context.app.adIdGenerator.getUniqueId();
  }

  componentDidMount() {
    /* eslint react/no-did-mount-set-state: 0 */
    if (this.adId !== 'takeover' && this.adId !== 'ticker') {
      this.setState({
        maxWidth: this.refs.ad.clientWidth || this.refs.ad.offsetWidth,
      });
    } else if (this.props.maxWidth) {
      this.centerTakeover(this.props.maxWidth);
    }

    pubSub.publish('displayAdRendered', this.adId);

    this.fwInterval = setInterval(this.freewheelCheck, 50);
    this.freewheelCheck();

    if (this.props.isActiveSlot) {
      this.refs.fwph.setAttribute('classname', '_fwph_r');
    }

    this.adInterval = setInterval(this.adCheck, 50);
    this.adCheck();

    // The initAdSession function in adUtils will publish the 'disableAdUnits' event
    // after 3s if there are any ad units that haven't received a display ad by then
    this.disableAdUnitsSubscription = pubSub.subscribe('disableAdUnits', (emptyUnits) => {
      if (emptyUnits[this.adId]) {
        if (!this.state.disabled) this.setAdHeight(0);
        // Disable this ad unit if it's one of the empty ad units
        this.disable();
        // Clear adInterval
        clearInterval(this.adInterval);
      }
    });

    if (this.adId === 'ticker') {
      // Listen to messages from ticker ad iframes
      window.addEventListener('message', this.receiveMessage);
    }

    if (AD_HEIGHTS[this.adId]) this.setAdHeight(AD_HEIGHTS[this.adId]);
  }

  componentWillReceiveProps({ disable, maxWidth }) {
    if (disable) {
      this.disable();
    }

    if (this.adId === 'takeover' && maxWidth && !this.props.maxWidth) {
      this.centerTakeover(maxWidth);
    }
  }

  componentWillUnmount() {
    clearInterval(this.fwInterval);
    clearInterval(this.adInterval);
    this.disableAdUnitsSubscription.remove();

    if (this.adId === 'ticker') {
      window.removeEventListener('message', this.receiveMessage);
      this.refs.ad.style.height = '0px';
      pubSub.publish('tickerHeightChanged');
    }
  }

  setAdHeight(height) {
    AD_HEIGHTS[this.adId] = height;
    this.refs.ad.style.height = `${height}px`;

    if (this.adId === 'ticker') {
      pubSub.publish('tickerHeightChanged');
    }
  }

  disable() {
    if (this.state.disabled !== '') return;

    clearInterval(this.fwInterval);

    this.setState({
      disabled: 'disabled',
      width: 'auto',
    }, () => {
      if (this.props.callback) this.props.callback();
      pubSub.publish('displayAdLoaded', this.adId);
    });
  }

  showAd(size = {}) {
    this.setState({
      showAd: true,
    }, () => {
      // Sometimes there is a data-height attribute, which is needed when the ticker is an image...
      const heightFromAttr = this.adId === 'ticker' ? this.refs.adContainer.childNodes[0].getAttribute('data-height') : 0;
      this.refs.ad.style.height = 'auto';
      const adHeight = parseInt(size.height || heightFromAttr || this.refs.ad.clientHeight || 0, 10);
      this.setAdHeight(adHeight);

      const scrollTop = getScrollTop();
      const adOffset = adHeight + scrollTop;

      if (adOffset <= scrollTop) {
        window.scroll(0, Math.max(0, scrollTop + adHeight));
      } else {
        // Publish current scroll value to force update fixed components
        pubSub.publish('scroll', scrollTop);
      }

      this.props.dispatch(displayAdLoaded());
    });
  }

  centerTakeover(maxWidth) {
    this.setState({
      width: maxWidth,
      marginLeft: maxWidth / 2,
      containerWidth: maxWidth,
    });
  }

  freewheelCheck() {
    if (this.refs.adContainer.childNodes.length) {
      this.disable();
    }
  }

  adCheck() {
    const image = this.refs.adContainer.getElementsByTagName('img')[0];
    const iframe = this.refs.adContainer.getElementsByTagName('iframe')[0];
    const div = this.refs.adContainer.querySelectorAll('div')[0];
    const isDK = this.props.country === 'dk';

    // Special case for adsambler in denmark who use arbitrary size in units.
    

    if (image) {
      if (image.complete || image.naturalWidth) {
        this.showAd();
      } else {
        image.onload = () => this.showAd();
      }
      clearInterval(this.adInterval);
    } else if (iframe && iframe.clientHeight) {
      this.showAd();
      clearInterval(this.adInterval);
    }
  }

  receiveMessage(event) {
    // Validate that the source of the message is the ticker ad iframe
    // and that the passed message is a hexadecimal color value
    const iframe = this.refs.adContainer.getElementsByTagName('iframe')[0];
    const { data } = event;

    if (
      !iframe ||
      event.source !== iframe.contentWindow ||
      !this.validateBackgroundColor(data)
    ) return;

    this.setState({
      backgroundColor: data,
    });
  }

  validateBackgroundColor(data) {
    return typeof data === 'string' && /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(data);
  }

  render() {
    const { maxWidth, width, backgroundColor, showAd } = this.state;
    const top = this.adId === 'takeover' ? this.props.headerHeight : null;

    return (
      <div
        ref="ad"
        className={cx('libellus', { 'is-visible': showAd })}
        style={{ maxWidth, width, backgroundColor }}
      >
        {
          showAd && this.adId !== 'takeover' && this.adId !== 'ticker' &&
            <p className="libellus-label">{this.context.app.t('AdUnit.ad_label')}:</p>
        }
        <span ref="fwph" id={`${this.adId}${this.state.disabled}`} className="_fwph">
          <form id={`_fw_form_${this.adId}`} style={{ display: 'none' }}>
            <input
              type="hidden"
              name={`_fw_input_${this.adId}`}
              id={`_fw_input_${this.adId}`}
              value={this.props.value}
            />
          </form>
          <span
            ref="adContainer"
            id={`_fw_container_${this.adId}`}
            style={{ marginLeft: -this.state.marginLeft, width: this.state.containerWidth, top }}
          ></span>
        </span>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/component/Ads/AdUnit.jsx